/**
 * Per-environment settings read when the app starts, not baked in at build time,
 * so one build serves every environment. Terraform writes `/config.json` into
 * each web bucket. Without it (dev server, a bucket nobody wrote one to) the
 * build-time `VITE_*` values apply, so builds made per environment keep working.
 *
 * `src/boot.ts` and `src/mobile/boot.ts` load it before importing the app,
 * because modules such as `apiBase.ts` read it while they load.
 */
export interface RuntimeConfig {
  apiUrl?: string
  procedureTemplateExport?: boolean
}

/** The last copy read online, so the offline mobile app still knows its API. */
const STORAGE_KEY = 'qms.runtimeConfig'

let current: RuntimeConfig = {}

export function runtimeConfig(): RuntimeConfig {
  return current
}

/** Only the known keys, of the right type: the file is outside the bundle. */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  const value = (raw ?? {}) as Record<string, unknown>
  return {
    ...(typeof value.apiUrl === 'string' && value.apiUrl ? { apiUrl: value.apiUrl } : {}),
    ...(typeof value.procedureTemplateExport === 'boolean'
      ? { procedureTemplateExport: value.procedureTemplateExport }
      : {}),
  }
}

export async function loadRuntimeConfig(fetchConfig: typeof fetch = fetch): Promise<RuntimeConfig> {
  let res: Response
  try {
    res = await fetchConfig('/config.json', { cache: 'no-cache' })
  } catch {
    // No connection: the copy from the last start that had one.
    try {
      current = parseRuntimeConfig(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'))
    } catch {
      current = {}
    }
    return current
  }
  // A missing key comes back as the app's index.html, so anything but JSON means "none".
  const isJson = res.ok && (res.headers.get('content-type') ?? '').includes('json')
  current = isJson ? parseRuntimeConfig(await res.json().catch(() => ({}))) : {}
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
  } catch {
    // Storage full or blocked: only the offline start loses the copy.
  }
  return current
}
